import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Coins,
  FileText,
  Mail,
  Music,
  Plus,
  Save,
  Trash2,
  Upload,
  UserCheck,
  X,
} from 'lucide-react';
import { Btn, IconButton } from '../ui';
import { Select, TextField } from '../forms';
import {
  BONUS_INLINE_MAX_TEXT_FILES,
  BONUS_INLINE_MAX_TEXT_LABEL,
  sumBonusInlineTextChars,
  validateBonusInlineTextFiles,
} from '../../lib/bonusInlineAssetPolicy';
import {
  BONUS_SAMPLE_PROMPT_MAX_ENTRIES,
  validateBonusSamplePrompts,
} from '../../lib/bonusSamplePrompts';
import { focusableWithin, wrapTabFocus } from '../../lib/focusTrap';
import { defaultBonusAnnouncementEmail } from '../../lib/bonusAnnouncementEmail';
import {
  bonusVoicePackContentsValue,
  bonusVoicePackTitlesValue,
  parseBonusVoicePacks,
} from '../../lib/bonusVoicePacks';
import { c } from '../../styles/theme';
import type {
  BonusAudienceFilter,
  BonusAnnouncementEmailDraft,
  BonusClaimRecipient,
  BonusCreditGrant,
  BonusPack,
  BonusPurchasePlatform,
  BonusSamplePrompt,
  BonusTextFile,
} from '../../types/domain';

const FIELD_STYLE = { padding: '8px 10px' } as const;
const MAX_SPECIFIC_AUDIENCE_EMAILS = 500;
const KIND_OPTIONS = [
  { value: 'lyrics-template-file', label: 'Lyrics templates' },
  { value: 'voice-pack-placeholder', label: 'Voice pack' },
  { value: 'sample-pack-files', label: 'Sample pack' },
  { value: 'credit-pack', label: 'Credit pack' },
] as const;
const PURCHASE_PLATFORM_OPTIONS: { value: BonusPurchasePlatform; label: string }[] = [
  { value: 'jvzoo', label: 'JVZoo' },
  { value: 'launchpadjv', label: 'Launchpad JV' },
  { value: 'gumroad', label: 'Gumroad' },
  { value: 'admin', label: 'Admin' },
];

function defaultCreditGrant(): BonusCreditGrant {
  return {
    amount: 10,
    kind: 'generation',
    audience: defaultAudience(),
  };
}

function defaultAudience(): BonusAudienceFilter {
  return { levels: [], verification: 'any', purchasePlatforms: [] };
}

function parseSpecificAudienceEmails(value: string): { emails?: string[]; error?: string } {
  const emails = [
    ...new Set(
      value
        .split(',')
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
  if (emails.length === 0) return { error: 'Enter at least one audience email address.' };
  if (emails.length > MAX_SPECIFIC_AUDIENCE_EMAILS) {
    return {
      error: `Enter no more than ${MAX_SPECIFIC_AUDIENCE_EMAILS} audience email addresses.`,
    };
  }
  if (emails.some((email) => email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
    return { error: 'Enter valid email addresses separated by commas.' };
  }
  return { emails };
}

function defaultTextFile(index = 0): BonusTextFile {
  return {
    fileName: index === 0 ? 'lyric-starters.txt' : `lyric-starters-${index + 1}.txt`,
    mimeType: 'text/plain',
    contents: '',
  };
}

function defaultSamplePrompt(): BonusSamplePrompt {
  return { title: '', prompt: '' };
}

function shouldReplaceSeedTextFiles(files: readonly BonusTextFile[]): boolean {
  return (
    files.length > 0 &&
    files.every(
      (file, index) =>
        file.fileName.trim() === defaultTextFile(index).fileName && file.contents.trim() === ''
    )
  );
}

async function readTextUpload(file: File): Promise<BonusTextFile> {
  if (!file.name.trim().toLowerCase().endsWith('.txt')) {
    throw new Error('Lyric template uploads must be .txt files.');
  }
  return {
    fileName: file.name.trim(),
    mimeType: 'text/plain',
    contents: await file.text(),
  };
}

const EMPTY_PACK: BonusPack = {
  id: '',
  name: '',
  desc: '',
  kind: 'lyrics-template-file',
  published: true,
  audience: defaultAudience(),
  textFiles: [defaultTextFile()],
};

export interface AdminBonusPackEditorProps {
  mode: 'create' | 'edit';
  pack: BonusPack | null;
  pending: boolean;
  claimRecipients: BonusClaimRecipient[];
  claimsStatus: 'idle' | 'loading' | 'ready' | 'error';
  onClose: () => void;
  onPreviewEmail: (pack: BonusPack) => Promise<string>;
  onSave: (pack: BonusPack) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function legacyTextFile(pack: BonusPack): BonusTextFile | undefined {
  return (pack as BonusPack & { textFile?: BonusTextFile }).textFile;
}

function normalizedTextFiles(pack: BonusPack): BonusTextFile[] {
  if (Array.isArray(pack.textFiles)) {
    return pack.textFiles.map((file) => ({ ...file, mimeType: 'text/plain' }));
  }
  const legacy = legacyTextFile(pack);
  return legacy ? [{ ...legacy, mimeType: 'text/plain' }] : [];
}

function normalizeForKind(pack: BonusPack, kind: BonusPack['kind']): BonusPack {
  const audience = pack.audience ?? pack.creditGrant?.audience ?? defaultAudience();
  if (kind === 'credit-pack') {
    const creditGrant = pack.creditGrant ?? defaultCreditGrant();
    return {
      ...pack,
      kind,
      audience,
      textFiles: undefined,
      samplePrompts: undefined,
      voicePacks: undefined,
      creditGrant: { ...creditGrant, audience },
    };
  }
  if (kind === 'lyrics-template-file') {
    return {
      ...pack,
      kind,
      audience,
      samplePrompts: undefined,
      voicePacks: undefined,
      creditGrant: undefined,
      textFiles:
        normalizedTextFiles(pack).length > 0 ? normalizedTextFiles(pack) : [defaultTextFile()],
    };
  }
  if (kind === 'sample-pack-files') {
    return {
      ...pack,
      kind,
      audience,
      textFiles: undefined,
      voicePacks: undefined,
      samplePrompts:
        Array.isArray(pack.samplePrompts) && pack.samplePrompts.length > 0
          ? pack.samplePrompts
          : [defaultSamplePrompt()],
      creditGrant: undefined,
    };
  }
  return {
    ...pack,
    kind,
    audience,
    textFiles: undefined,
    samplePrompts: undefined,
    voicePacks: Array.isArray(pack.voicePacks) ? pack.voicePacks : [],
    note: pack.note ?? '',
    creditGrant: undefined,
  };
}

function initialDraft(pack: BonusPack | null): BonusPack {
  const base = pack ? clone(pack) : clone(EMPTY_PACK);
  const normalized = normalizeForKind(base, base.kind);
  return {
    ...normalized,
    announcementEmail: normalized.announcementEmail ?? defaultBonusAnnouncementEmail(normalized),
  };
}

export default function AdminBonusPackEditor({
  mode,
  pack,
  pending,
  claimRecipients,
  claimsStatus,
  onClose,
  onPreviewEmail,
  onSave,
  onDelete,
}: AdminBonusPackEditorProps) {
  const [draft, setDraft] = useState<BonusPack>(() => initialDraft(pack));
  const [voicePackTitles, setVoicePackTitles] = useState(() =>
    bonusVoicePackTitlesValue(initialDraft(pack).voicePacks)
  );
  const [voicePackContents, setVoicePackContents] = useState(() =>
    bonusVoicePackContentsValue(initialDraft(pack).voicePacks)
  );
  const [error, setError] = useState('');
  const [audienceMode, setAudienceMode] = useState<'filters' | 'emails'>(() =>
    initialDraft(pack).audience?.specificEmails?.length ? 'emails' : 'filters'
  );
  const [audienceEmails, setAudienceEmails] = useState(
    () => initialDraft(pack).audience?.specificEmails?.join(', ') ?? ''
  );
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewFingerprint, setPreviewFingerprint] = useState('');
  const [previewPending, setPreviewPending] = useState(false);
  const currentPreviewFingerprint = JSON.stringify([
    draft,
    voicePackTitles,
    voicePackContents,
    audienceMode,
    audienceEmails,
  ]);
  const currentPreviewFingerprintRef = useRef(currentPreviewFingerprint);
  currentPreviewFingerprintRef.current = currentPreviewFingerprint;
  const hasCurrentPreview =
    previewHtml.length > 0 && previewFingerprint === currentPreviewFingerprint;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const textFileInputRef = useRef<HTMLInputElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const headingId = useId();

  useEffect(() => {
    const nextDraft = initialDraft(pack);
    setDraft(nextDraft);
    setVoicePackTitles(bonusVoicePackTitlesValue(nextDraft.voicePacks));
    setVoicePackContents(bonusVoicePackContentsValue(nextDraft.voicePacks));
    setAudienceMode(nextDraft.audience?.specificEmails?.length ? 'emails' : 'filters');
    setAudienceEmails(nextDraft.audience?.specificEmails?.join(', ') ?? '');
    setError('');
    setPreviewHtml('');
    setPreviewFingerprint('');
  }, [pack, mode]);

  useEffect(() => {
    setPreviewHtml('');
    setPreviewFingerprint('');
  }, [draft, voicePackTitles, voicePackContents, audienceMode, audienceEmails]);

  useEffect(() => {
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = rootRef.current;
    if (root) focusableWithin(root)[0]?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      const current = rootRef.current;
      if (!current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      wrapTabFocus(current, e);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target?.isConnected) target.focus();
    };
  }, [onClose]);

  const samplePromptCount = useMemo(() => draft.samplePrompts?.length ?? 0, [draft.samplePrompts]);
  const textFileCount = useMemo(() => draft.textFiles?.length ?? 0, [draft.textFiles]);
  const textTotalChars = useMemo(
    () => sumBonusInlineTextChars(draft.textFiles ?? []),
    [draft.textFiles]
  );

  function handleKindChange(kind: BonusPack['kind']) {
    const normalized = normalizeForKind(draft, kind);
    setDraft(normalized);
    setVoicePackTitles(bonusVoicePackTitlesValue(normalized.voicePacks));
    setVoicePackContents(bonusVoicePackContentsValue(normalized.voicePacks));
    setError('');
  }

  function patch(next: Partial<BonusPack>) {
    setDraft((current) => ({ ...current, ...next }));
    setError('');
  }

  function patchCreditGrant(next: Partial<BonusCreditGrant>) {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'credit-pack');
      return {
        ...normalized,
        creditGrant: { ...(normalized.creditGrant ?? defaultCreditGrant()), ...next },
      };
    });
    setError('');
  }

  function patchAudience(next: Partial<BonusAudienceFilter>) {
    setDraft((current) => {
      const audience = { ...(current.audience ?? defaultAudience()), ...next };
      return {
        ...current,
        audience,
        ...(current.creditGrant ? { creditGrant: { ...current.creditGrant, audience } } : {}),
      };
    });
    setError('');
  }

  function patchAnnouncementEmail(next: Partial<BonusAnnouncementEmailDraft>) {
    setDraft((current) => ({
      ...current,
      announcementEmail: {
        ...(current.announcementEmail ?? defaultBonusAnnouncementEmail(current)),
        ...next,
      },
    }));
    setError('');
  }

  function patchTextFile(index: number, next: Partial<BonusTextFile>) {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'lyrics-template-file');
      const files = [...(normalized.textFiles ?? [defaultTextFile()])];
      const currentFile = files[index] ?? defaultTextFile(index);
      files[index] = { ...currentFile, ...next, mimeType: 'text/plain' };
      return { ...normalized, textFiles: files };
    });
    setError('');
  }

  function addTextFile() {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'lyrics-template-file');
      const files = normalized.textFiles ?? [defaultTextFile()];
      return { ...normalized, textFiles: [...files, defaultTextFile(files.length)] };
    });
    setError('');
  }

  function removeTextFile(index: number) {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'lyrics-template-file');
      const files = (normalized.textFiles ?? [defaultTextFile()]).filter(
        (_, candidateIndex) => candidateIndex !== index
      );
      return { ...normalized, textFiles: files.length > 0 ? files : [defaultTextFile()] };
    });
    setError('');
  }

  function patchSamplePrompt(index: number, next: Partial<BonusSamplePrompt>) {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'sample-pack-files');
      const prompts = [...(normalized.samplePrompts ?? [defaultSamplePrompt()])];
      prompts[index] = { ...(prompts[index] ?? defaultSamplePrompt()), ...next };
      return { ...normalized, samplePrompts: prompts };
    });
    setError('');
  }

  function addSamplePrompt() {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'sample-pack-files');
      const prompts = normalized.samplePrompts ?? [defaultSamplePrompt()];
      if (prompts.length >= BONUS_SAMPLE_PROMPT_MAX_ENTRIES) return normalized;
      return { ...normalized, samplePrompts: [...prompts, defaultSamplePrompt()] };
    });
    setError('');
  }

  function removeSamplePrompt(index: number) {
    setDraft((current) => {
      const normalized = normalizeForKind(current, 'sample-pack-files');
      const prompts = (normalized.samplePrompts ?? []).filter(
        (_, candidateIndex) => candidateIndex !== index
      );
      return {
        ...normalized,
        samplePrompts: prompts.length > 0 ? prompts : [defaultSamplePrompt()],
      };
    });
    setError('');
  }

  async function handleTextUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    try {
      const uploaded = await Promise.all(Array.from(files).map((file) => readTextUpload(file)));
      setDraft((current) => {
        const normalized = normalizeForKind(current, 'lyrics-template-file');
        const currentFiles = normalized.textFiles ?? [defaultTextFile()];
        const nextFiles = shouldReplaceSeedTextFiles(currentFiles)
          ? uploaded
          : [...currentFiles, ...uploaded];
        return {
          ...normalized,
          textFiles: nextFiles,
        };
      });
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The text files could not be added.');
    }
  }

  function preparedDraft(): BonusPack | null {
    const normalized = normalizeForKind(draft, draft.kind);
    if (!normalized.name.trim() || !normalized.desc.trim()) {
      setError('Add the bonus name and description first.');
      return null;
    }
    const email = normalized.announcementEmail;
    if (!email?.subject.trim() || !email.heading.trim() || !email.message.trim()) {
      setError('Complete the announcement email before previewing it.');
      return null;
    }
    normalized.announcementEmail = {
      subject: email.subject.trim(),
      heading: email.heading.trim(),
      message: email.message.trim(),
    };
    const currentAudience = normalized.audience ?? defaultAudience();
    if (audienceMode === 'emails') {
      const parsed = parseSpecificAudienceEmails(audienceEmails);
      if (parsed.error || !parsed.emails) {
        setError(parsed.error ?? 'Enter at least one audience email address.');
        return null;
      }
      normalized.audience = { ...currentAudience, specificEmails: parsed.emails };
    } else {
      const { specificEmails: _specificEmails, ...filteredAudience } = currentAudience;
      normalized.audience = filteredAudience;
    }
    if (normalized.creditGrant) {
      normalized.creditGrant = { ...normalized.creditGrant, audience: normalized.audience };
    }
    if (normalized.kind === 'lyrics-template-file') {
      const validation = validateBonusInlineTextFiles(normalized.textFiles ?? []);
      if (validation) {
        setError(validation);
        return null;
      }
    }
    if (normalized.kind === 'sample-pack-files') {
      const validation = validateBonusSamplePrompts(normalized.samplePrompts ?? []);
      if (validation) {
        setError(validation);
        return null;
      }
    }
    if (normalized.kind === 'voice-pack-placeholder') {
      const parsed = parseBonusVoicePacks(voicePackTitles, voicePackContents);
      if (parsed.error) {
        setError(parsed.error);
        return null;
      }
      normalized.voicePacks = parsed.voicePacks;
    }
    return normalized;
  }

  async function handlePreviewEmail() {
    const normalized = preparedDraft();
    if (!normalized || previewPending) return;
    const requestedFingerprint = currentPreviewFingerprint;
    setPreviewPending(true);
    setError('');
    try {
      const html = await onPreviewEmail(normalized);
      if (currentPreviewFingerprintRef.current !== requestedFingerprint) return;
      setPreviewHtml(html);
      setPreviewFingerprint(requestedFingerprint);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The email preview could not be loaded.');
    } finally {
      setPreviewPending(false);
    }
  }

  async function handleSave() {
    try {
      const normalized = preparedDraft();
      if (!normalized) return;
      if (mode === 'create' && !hasCurrentPreview) {
        setError('Preview the current announcement email before creating the bonus pack.');
        return;
      }
      await onSave(normalized);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The bonus pack could not be saved.');
    }
  }

  return (
    <>
      <div className="lyx-drawer-overlay" onClick={onClose} aria-hidden="true" />
      <div
        ref={rootRef}
        className="lyx-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        style={{ background: c.surface1, borderLeft: `1px solid ${c.border}` }}
      >
        <div style={{ padding: '18px 20px 32px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 14,
            }}
          >
            <p
              id={headingId}
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 15,
                margin: 0,
              }}
            >
              {mode === 'create' ? 'Create bonus pack' : 'Edit bonus pack'}
              <span className="lyx-visually-hidden"> — {draft.name || 'Bonus pack editor'}</span>
            </p>
            <IconButton label="Close" onClick={onClose}>
              <X size={18} color={c.text3} />
            </IconButton>
          </div>

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: '12px 14px',
              background: c.surface2,
              marginBottom: 14,
            }}
          >
            <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>Pack details</p>
            <div className="lyx-grid-2" style={{ marginBottom: 10 }}>
              <TextField
                label="Name"
                value={draft.name}
                onChange={(name) => patch({ name })}
                style={FIELD_STYLE}
              />
              <Select
                label="Pack type"
                value={draft.kind}
                onChange={(value) => handleKindChange(value as BonusPack['kind'])}
                options={
                  mode === 'edit' && pack?.kind !== 'credit-pack'
                    ? KIND_OPTIONS.filter((option) => option.value !== 'credit-pack')
                    : KIND_OPTIONS
                }
                disabled={mode === 'edit' && pack?.kind === 'credit-pack'}
                style={FIELD_STYLE}
              />
            </div>
            <TextField
              label="Description"
              value={draft.desc}
              onChange={(desc) => patch({ desc })}
              multiline
              rows={4}
              style={FIELD_STYLE}
              marginBottom={10}
            />
            <div
              className={draft.kind === 'credit-pack' ? undefined : 'lyx-grid-2'}
              style={{ marginBottom: 0 }}
            >
              {draft.kind !== 'credit-pack' && (
                <Select
                  label="Visibility"
                  value={draft.published === false ? 'unpublished' : 'published'}
                  onChange={(value) => patch({ published: value === 'published' })}
                  options={[
                    { value: 'published', label: 'Published' },
                    { value: 'unpublished', label: 'Unpublished' },
                  ]}
                  style={FIELD_STYLE}
                />
              )}
              <TextField
                label="Admin note"
                value={draft.note ?? ''}
                onChange={(note) => patch({ note })}
                multiline
                rows={4}
                style={FIELD_STYLE}
              />
            </div>
          </div>

          {draft.kind === 'credit-pack' && draft.creditGrant && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Coins size={14} color={c.text2} />
                <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Permanent credit delivery</p>
              </div>
              {mode === 'edit' ? (
                <>
                  <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
                    Offers {draft.creditGrant.amount.toLocaleString('en-US')}{' '}
                    {draft.creditGrant.kind === 'publisher' ? 'Publisher' : 'generation'} credits to{' '}
                    {draft.creditGrant.recipientCount ?? 0} users when claimed. Delivery settings
                    cannot be changed after creation.
                  </p>
                  <p style={{ fontSize: 11, color: c.text3, margin: '6px 0 0' }}>
                    Email delivery: {draft.creditGrant.emailSentCount ?? 0} sent,{' '}
                    {draft.creditGrant.emailFailedCount ?? 0} failed.
                  </p>
                </>
              ) : (
                <>
                  <div className="lyx-grid-2" style={{ marginBottom: 12 }}>
                    <TextField
                      label="Credits per user"
                      value={String(draft.creditGrant.amount)}
                      onChange={(value) =>
                        patchCreditGrant({ amount: Number.parseInt(value, 10) || 0 })
                      }
                      style={FIELD_STYLE}
                    />
                    <Select
                      label="Credit type"
                      value={draft.creditGrant.kind}
                      onChange={(value) =>
                        patchCreditGrant({ kind: value as BonusCreditGrant['kind'] })
                      }
                      options={[
                        { value: 'generation', label: 'Generation credits' },
                        { value: 'publisher', label: 'Publisher credits' },
                      ]}
                      style={FIELD_STYLE}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {mode === 'edit' && (
            <div
              aria-label="Bonus claims"
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginBottom: claimsStatus === 'ready' && claimRecipients.length > 0 ? 8 : 0,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <UserCheck size={14} color={c.text2} />
                  <span style={{ fontSize: 12, color: c.text2 }}>Claimed by</span>
                </span>
                {claimsStatus === 'ready' && (
                  <span style={{ fontSize: 11, color: c.text3 }}>
                    {claimRecipients.length.toLocaleString('en-US')}{' '}
                    {claimRecipients.length === 1 ? 'user' : 'users'}
                  </span>
                )}
              </div>

              {claimsStatus === 'loading' && (
                <p style={{ fontSize: 11.5, color: c.text3, margin: '8px 0 0' }}>
                  Loading claim recipients…
                </p>
              )}
              {claimsStatus === 'error' && (
                <p role="alert" style={{ fontSize: 11.5, color: c.danger, margin: '8px 0 0' }}>
                  Claim recipients could not be loaded.
                </p>
              )}
              {claimsStatus === 'ready' && claimRecipients.length === 0 && (
                <p style={{ fontSize: 11.5, color: c.text3, margin: '8px 0 0' }}>
                  No users have claimed this bonus yet.
                </p>
              )}
              {claimsStatus === 'ready' && claimRecipients.length > 0 && (
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {claimRecipients.map((recipient, index) => (
                    <div
                      key={recipient.userId}
                      style={{
                        display: 'grid',
                        gap: 2,
                        padding: '8px 0',
                        borderTop: index === 0 ? 'none' : `1px solid ${c.border}`,
                      }}
                    >
                      <span style={{ fontSize: 12, color: c.text1 }}>{recipient.name}</span>
                      <span style={{ fontSize: 11, color: c.text3, overflowWrap: 'anywhere' }}>
                        {recipient.email}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {draft.audience && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <p style={{ fontSize: 12, color: c.text2, margin: '0 0 10px' }}>Bonus audience</p>
              {mode === 'edit' && draft.kind === 'credit-pack' ? (
                <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
                  The claimable credit audience cannot be changed after creation.
                </p>
              ) : (
                <>
                  <Select
                    label="Audience selection"
                    value={audienceMode}
                    onChange={(value) => {
                      setAudienceMode(value as 'filters' | 'emails');
                      setError('');
                    }}
                    options={[
                      { value: 'filters', label: 'Use filters' },
                      { value: 'emails', label: 'Specific emails' },
                    ]}
                    style={FIELD_STYLE}
                    marginBottom={12}
                  />
                  {audienceMode === 'emails' ? (
                    <TextField
                      label="Specific user emails (comma-separated)"
                      value={audienceEmails}
                      onChange={(value) => {
                        setAudienceEmails(value);
                        setError('');
                      }}
                      multiline
                      rows={4}
                      style={FIELD_STYLE}
                    />
                  ) : (
                    <>
                      <Select
                        label="Verification status"
                        value={draft.audience.verification}
                        onChange={(verification) =>
                          patchAudience({
                            verification: verification as BonusAudienceFilter['verification'],
                          })
                        }
                        options={[
                          { value: 'any', label: 'Any status' },
                          { value: 'verified', label: 'Verified only' },
                          { value: 'unverified', label: 'Unverified only' },
                        ]}
                        style={FIELD_STYLE}
                        marginBottom={12}
                      />
                      <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
                        <legend style={{ fontSize: 12, color: c.text2, marginBottom: 6 }}>
                          Level status (none means any)
                        </legend>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                          {Array.from({ length: 9 }, (_, level) => (
                            <label key={level} style={{ fontSize: 12, color: c.text1 }}>
                              <input
                                type="checkbox"
                                checked={draft.audience!.levels.includes(level)}
                                onChange={(event) =>
                                  patchAudience({
                                    levels: event.target.checked
                                      ? [...draft.audience!.levels, level]
                                      : draft.audience!.levels.filter(
                                          (candidate) => candidate !== level
                                        ),
                                  })
                                }
                              />{' '}
                              Level {level}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
                        <legend style={{ fontSize: 12, color: c.text2, marginBottom: 6 }}>
                          Purchase platform (none means any)
                        </legend>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px' }}>
                          {PURCHASE_PLATFORM_OPTIONS.map((platform) => (
                            <label key={platform.value} style={{ fontSize: 12, color: c.text1 }}>
                              <input
                                type="checkbox"
                                checked={draft.audience!.purchasePlatforms.includes(platform.value)}
                                onChange={(event) =>
                                  patchAudience({
                                    purchasePlatforms: event.target.checked
                                      ? [...draft.audience!.purchasePlatforms, platform.value]
                                      : draft.audience!.purchasePlatforms.filter(
                                          (candidate) => candidate !== platform.value
                                        ),
                                  })
                                }
                              />{' '}
                              {platform.label}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <div className="lyx-grid-2">
                        <TextField
                          label="Joined from"
                          type="date"
                          value={draft.audience.joinedFrom ?? ''}
                          onChange={(joinedFrom) => patchAudience({ joinedFrom })}
                          style={FIELD_STYLE}
                        />
                        <TextField
                          label="Joined to"
                          type="date"
                          value={draft.audience.joinedTo ?? ''}
                          onChange={(joinedTo) => patchAudience({ joinedTo })}
                          style={FIELD_STYLE}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {draft.kind === 'lyrics-template-file' && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FileText size={14} color={c.text2} />
                <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Lyrics template files</p>
              </div>
              <p style={{ fontSize: 11.5, color: c.text3, margin: '0 0 10px', lineHeight: 1.5 }}>
                Upload one or more <strong>.txt</strong> files, or paste text manually below. Keep
                the whole pack under {BONUS_INLINE_MAX_TEXT_LABEL} so it saves safely in local
                memory mode.
              </p>
              <input
                ref={textFileInputRef}
                type="file"
                accept=".txt,text/plain"
                multiple
                className="lyx-visually-hidden"
                aria-label="Upload lyric template text files"
                onChange={(e) => {
                  void handleTextUpload(e.target.files);
                  e.target.value = '';
                }}
              />
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  marginBottom: 10,
                }}
              >
                <Btn
                  variant="secondary"
                  onClick={() => textFileInputRef.current?.click()}
                  disabled={pending}
                >
                  <Upload size={13} />
                  Upload TXT files
                </Btn>
                <Btn variant="secondary" onClick={addTextFile} disabled={pending}>
                  <Plus size={13} />
                  Add template file
                </Btn>
              </div>
              <p style={{ fontSize: 11, color: c.text3, margin: '0 0 8px' }}>
                {textFileCount} of {BONUS_INLINE_MAX_TEXT_FILES} files ·{' '}
                {textTotalChars.toLocaleString('en-US')} of {BONUS_INLINE_MAX_TEXT_LABEL} stored
                inline
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(draft.textFiles ?? []).map((file, index) => (
                  <div
                    key={`text-file-${index}`}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${c.border}`,
                      background: c.surface1,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <p style={{ fontSize: 12.5, fontWeight: 500, margin: 0 }}>
                        Template file {index + 1}
                      </p>
                      <Btn
                        variant="secondary"
                        onClick={() => removeTextFile(index)}
                        disabled={pending || (draft.textFiles?.length ?? 0) <= 1}
                        style={{ color: c.danger, flexShrink: 0 }}
                      >
                        <Trash2 size={13} />
                        Remove
                      </Btn>
                    </div>
                    <TextField
                      label={`File name ${index + 1}`}
                      value={file.fileName}
                      onChange={(fileName) => patchTextFile(index, { fileName })}
                      style={FIELD_STYLE}
                      marginBottom={10}
                    />
                    <TextField
                      label={`TXT contents ${index + 1}`}
                      value={file.contents}
                      onChange={(contents) => patchTextFile(index, { contents })}
                      multiline
                      rows={8}
                      style={{ ...FIELD_STYLE, fontFamily: 'IBM Plex Mono', fontSize: 11.5 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {draft.kind === 'voice-pack-placeholder' && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Music size={14} color={c.text2} />
                <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Voice pack prompts</p>
              </div>
              <p style={{ fontSize: 11.5, color: c.text3, margin: '0 0 10px', lineHeight: 1.5 }}>
                Add pipe-separated Titles and Contents. Each title must exactly match one content
                entry, and one claimed pack will open into many child prompt packs.
              </p>
              <TextField
                label="Titles"
                value={voicePackTitles}
                onChange={(value) => {
                  setVoicePackTitles(value);
                  setError('');
                }}
                multiline
                rows={4}
                style={FIELD_STYLE}
                marginBottom={10}
              />
              <TextField
                label="Contents"
                value={voicePackContents}
                onChange={(value) => {
                  setVoicePackContents(value);
                  setError('');
                }}
                multiline
                rows={8}
                style={{ ...FIELD_STYLE, fontFamily: 'IBM Plex Mono', fontSize: 11.5 }}
              />
            </div>
          )}

          {draft.kind === 'sample-pack-files' && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Music size={14} color={c.text2} />
                <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Instrumental loop prompts</p>
              </div>
              <p style={{ fontSize: 11.5, color: c.text3, margin: '0 0 10px', lineHeight: 1.5 }}>
                Give every loop idea a clear title and a precise generation prompt. A sample pack
                can contain up to {BONUS_SAMPLE_PROMPT_MAX_ENTRIES} prompts for users to choose
                from.
              </p>
              <Btn
                variant="secondary"
                onClick={addSamplePrompt}
                disabled={pending || samplePromptCount >= BONUS_SAMPLE_PROMPT_MAX_ENTRIES}
                style={{ marginBottom: 10 }}
              >
                <Plus size={13} />
                Add loop prompt
              </Btn>
              <p style={{ fontSize: 11, color: c.text3, margin: '0 0 8px' }}>
                {samplePromptCount} of {BONUS_SAMPLE_PROMPT_MAX_ENTRIES} prompts
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(draft.samplePrompts ?? []).map((entry, index) => (
                  <div
                    key={`sample-prompt-${index}`}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: `1px solid ${c.border}`,
                      background: c.surface1,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <p style={{ fontSize: 12.5, fontWeight: 500, margin: 0 }}>
                        Loop prompt {index + 1}
                      </p>
                      <Btn
                        variant="secondary"
                        onClick={() => removeSamplePrompt(index)}
                        disabled={pending || samplePromptCount <= 1}
                        style={{ color: c.danger, flexShrink: 0 }}
                      >
                        <Trash2 size={13} />
                        Remove
                      </Btn>
                    </div>
                    <TextField
                      label={`Loop title ${index + 1}`}
                      value={entry.title}
                      onChange={(title) => patchSamplePrompt(index, { title })}
                      style={FIELD_STYLE}
                      marginBottom={10}
                    />
                    <TextField
                      label={`Loop prompt ${index + 1}`}
                      value={entry.prompt}
                      onChange={(prompt) => patchSamplePrompt(index, { prompt })}
                      multiline
                      rows={8}
                      style={{ ...FIELD_STYLE, fontFamily: 'IBM Plex Mono', fontSize: 11.5 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === 'create' && draft.announcementEmail && (
            <div
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                padding: '12px 14px',
                background: c.surface2,
                marginBottom: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Mail size={14} color={c.text2} />
                <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Announcement email</p>
              </div>
              <TextField
                label="Email subject"
                value={draft.announcementEmail.subject}
                onChange={(subject) => patchAnnouncementEmail({ subject })}
                style={FIELD_STYLE}
                marginBottom={10}
              />
              <TextField
                label="Email heading"
                value={draft.announcementEmail.heading}
                onChange={(heading) => patchAnnouncementEmail({ heading })}
                style={FIELD_STYLE}
                marginBottom={10}
              />
              <TextField
                label="Email message"
                value={draft.announcementEmail.message}
                onChange={(message) => patchAnnouncementEmail({ message })}
                multiline
                rows={5}
                style={FIELD_STYLE}
                marginBottom={10}
              />
              <Btn
                variant="secondary"
                onClick={() => void handlePreviewEmail()}
                disabled={pending || previewPending}
              >
                <Mail size={13} />
                {previewPending ? 'Loading preview…' : 'Preview email'}
              </Btn>
              {hasCurrentPreview && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600 }}>Email preview</p>
                  <iframe
                    title="Bonus announcement email preview"
                    srcDoc={previewHtml}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 480,
                      border: `1px solid ${c.border}`,
                      borderRadius: 8,
                      background: '#fff',
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {error && (
            <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '0 0 12px' }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn
              onClick={() => void handleSave()}
              disabled={pending || (mode === 'create' && !hasCurrentPreview)}
            >
              {mode === 'create' ? <Plus size={13} /> : <Save size={13} />}
              {mode === 'create' ? 'Create bonus pack' : 'Save changes'}
            </Btn>
            <Btn variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Btn>
            {mode === 'edit' && draft.id && onDelete && (
              <Btn
                variant="secondary"
                onClick={() => {
                  void onDelete(draft.id).then(onClose);
                }}
                disabled={pending}
                style={{ color: c.danger }}
              >
                <Trash2 size={13} />
                Delete pack
              </Btn>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
